import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const AiDocumentationLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (data.user) {
        navigate("/documentation");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f8f4] via-white to-[#eef4ed] text-[#1f2a3d]">
      <div className="relative mx-auto flex max-w-5xl flex-col gap-10 px-6 pb-16 pt-12 sm:px-10 lg:flex-row lg:items-center lg:px-16">
        <div className="absolute left-6 top-6 h-36 w-36 rounded-full bg-[#8bdf9f]/30 blur-3xl" />
        <div className="absolute right-0 top-24 h-52 w-52 rounded-full bg-[#324705]/10 blur-3xl" />

        <div className="z-10 space-y-4 lg:w-1/2">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm font-semibold text-[#324705] shadow-md shadow-[#d8f7e0]">
            AI Medical Documentation
          </span>
          <h1 className="text-4xl font-semibold leading-tight sm:text-5xl">
            Log in to manage AI documentation.
          </h1>
          <p className="text-lg text-[#3b3d24] sm:text-xl">
            Access uploads, review drafts, and export structured notes to your
            EHR templates—all with HIPAA-first controls.
          </p>
          <div className="flex flex-wrap gap-3 text-sm text-[#3b3d24]">
            {["Audit trails", "Role-based access", "Configurable retention"].map(
              item => (
                <div key={item} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#3d874e]" />
                  {item}
                </div>
              )
            )}
          </div>
        </div>

        <div className="z-10 w-full lg:w-1/2">
          <div className="rounded-3xl bg-white/90 p-8 shadow-2xl shadow-[#d8f7e0] backdrop-blur">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-[#1f2a3d]">Log in</h2>
              <Link
                to="/ai-doc/signup"
                className="text-sm font-semibold text-[#3d874e] hover:underline"
              >
                Need access?
              </Link>
            </div>
            <p className="mt-2 text-sm text-[#3b3d24]">
              Use your Temple credentials to access the AI documentation pilot.
            </p>

            {error && (
              <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="text-sm font-semibold text-[#1f2a3d]">
                  Work email
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-2 w-full rounded-2xl border border-[#e7fbec] bg-white px-4 py-3 text-sm text-[#1f2a3d] focus:border-[#3d874e] focus:outline-none focus:ring-2 focus:ring-[#d8f7e0]"
                  placeholder="you@hospital.org"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1f2a3d]">
                  Password
                </label>
                <input
                  required
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="mt-2 w-full rounded-2xl border border-[#e7fbec] bg-white px-4 py-3 text-sm text-[#1f2a3d] focus:border-[#3d874e] focus:outline-none focus:ring-2 focus:ring-[#d8f7e0]"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center justify-between text-sm text-[#3b3d24]">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4 rounded border-[#e7fbec] text-[#3d874e] focus:ring-[#d8f7e0]" />
                  Remember me
                </label>
                <Link
                  to="/provider/forgot-password"
                  className="font-semibold text-[#3d874e] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#324705] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#324705]/20 transition hover:-translate-y-0.5 hover:bg-[#3d874e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Continue"}
              </button>
            </form>

            <div className="mt-6 rounded-2xl bg-[#f8f8f7] px-4 py-3 text-xs text-[#58594c]">
              We keep PHI encrypted, logged, and access-controlled. Need a BAA
              on file? Contact us during provisioning.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDocumentationLogin;
